// Shared URL helpers for runtime manifest/import/script security checks.
// Keeps protocol, origin, and asset-name rules aligned across runtime modules.
import { assertSecurityDependency, rethrowSecurityError } from './error'

const BLOCKED_PROTOCOLS = new Set(['javascript:', 'vbscript:', 'data:', 'blob:'])
const SAFE_NETWORK_PROTOCOLS = new Set(['http:', 'https:'])
const RUNTIME_ASSET_FILENAMES = new Set([
  'lentystyle.min.js',
  'lentystyle.debug.min.js',
  'lentystyle.performance.min.js',
  'lentystyle.map.min.js',
  'lentystyle.lazy.min.js',
  'lentystyle.observed.min.js',
  'lentystyle.worker.min.js',
  'lentystyle.compile.worker.min.js',
])

// Exposes one safe URL resolver for runtime-related absolute URL checks.
export function resolveRuntimeSecurityUrl(rawUrl: string, baseHref: string): URL | null {
  assertSecurityDependency(typeof URL === 'function', 'URL API')
  const normalized = rawUrl.trim()
  // Return null when the current URL value is empty after trim normalization.
  if (!normalized) {
    return null
  } else {
    // Continue URL resolution for the non-empty value.
  }

  try {
    return new URL(normalized, baseHref)
  } catch (error) {
    rethrowSecurityError(error)
    return null
  }
}

// Exposes whether one parsed URL uses a safe runtime network protocol.
export function isSafeRuntimeNetworkUrl(url: URL): boolean {
  const protocol = url.protocol.toLowerCase()
  // Reject blocked protocols before same-origin or allowlist checks.
  if (BLOCKED_PROTOCOLS.has(protocol)) {
    return false
  } else {
    // Continue safe-network protocol validation.
  }

  return SAFE_NETWORK_PROTOCOLS.has(protocol)
}

// Exposes whether one parsed URL belongs to the current browser origin.
export function isSameOriginRuntimeUrl(url: URL): boolean {
  assertSecurityDependency(typeof window !== 'undefined' && Boolean(window.location?.origin), 'window.location.origin')
  return url.origin === window.location.origin
}

// Exposes whether one parsed URL points at one known runtime asset filename.
export function isRuntimeAssetFilename(url: URL): boolean {
  return RUNTIME_ASSET_FILENAMES.has(getRuntimeAssetFilename(url))
}

// Exposes the last path segment used as the runtime asset filename.
export function getRuntimeAssetFilename(url: URL): string {
  return url.pathname.split('/').pop() ?? ''
}

// Exposes one normalized allowlist set built from configured origin strings.
export function normalizeRuntimeAllowedOrigins(allowlist: string[] | undefined): Set<string> {
  const normalized = new Set<string>()
  // Return an empty allowlist when no configured origins exist.
  if (!allowlist || allowlist.length === 0) {
    return normalized
  } else {
    // Continue origin normalization for configured allowlist values.
  }

  // Normalize each configured origin entry into one absolute origin string.
  for (const item of allowlist) {
    const value = item.trim()
    // Skip empty allowlist entries after trim normalization.
    if (!value) {
      continue
    } else {
      // Continue origin parsing for the non-empty allowlist entry.
    }

    try {
      normalized.add(new URL(value).origin)
    } catch {
      // Skip malformed origin entries silently.
    }
  }

  return normalized
}
